/**
 * Patient Meal Plan Card Component
 *
 * Displays a meal plan assignment for a patient in a card format.
 * Shows meal plan details, assignment info, and status.
 *
 * @module components/dietician-dashboard/PatientMealPlanCard
 */

import React, { useState } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { Card } from '@/components/ui/Card';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import type { AssignMealPlanResponseFrontend } from '@/types/mealPlan';

interface PatientMealPlanCardProps {
  assignment: AssignMealPlanResponseFrontend;
  onView?: (assignmentId: string) => void;
  onUnassign?: (assignmentId: string) => void;
  isUnassigning?: boolean;
  /** Custom title for the unassign confirmation dialog */
  unassignDialogTitle?: string;
  /** Custom message for the unassign confirmation dialog */
  unassignDialogMessage?: string;
}

/**
 * Patient Meal Plan Card Component
 */
export const PatientMealPlanCard: React.FC<PatientMealPlanCardProps> = ({
  assignment,
  onView,
  onUnassign,
  isUnassigning = false,
  unassignDialogTitle = 'Unassign Meal Plan',
  unassignDialogMessage = `Are you sure you want to unassign "${assignment.mealPlan.label || 'this meal plan'}" from ${assignment.patientName}? This action cannot be undone.`
}) => {
  const { isDark } = useTheme();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleUnassignClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmUnassign = () => {
    if (onUnassign) {
      onUnassign(assignment.id);
    }
    setShowConfirmDialog(false);
  };

  const handleCancelUnassign = () => {
    setShowConfirmDialog(false);
  };


  const getStatusBadge = (status: string) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    switch (status) {
      case 'active':
        return `${baseClasses} ${isDark ? 'bg-success-900/20 text-success-400' : 'bg-success-50 text-success-700'}`;
      case 'completed':
        return `${baseClasses} ${isDark ? 'bg-primary-900/20 text-primary-400' : 'bg-primary-50 text-primary-700'}`;
      case 'archived':
        return `${baseClasses} ${isDark ? 'bg-neutral-800 text-neutral-400' : 'bg-neutral-100 text-neutral-600'}`;
      default:
        return `${baseClasses} ${isDark ? 'bg-neutral-800 text-neutral-400' : 'bg-neutral-100 text-neutral-600'}`;
    }
  };

  return (
    <>
    <Card className="p-4 hover:shadow-lg transition-shadow duration-200">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <SubtitleText className="font-semibold mb-1">
            {assignment.mealPlan.label || 'Untitled Meal Plan'}
          </SubtitleText>
          <div className="flex items-center gap-2 mb-2">
            <span className={getStatusBadge(assignment.status)}>
              {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        {assignment.notes && (
          <div>
            <BodyText variant="secondary" size="sm" className="font-medium">
              Notes:
            </BodyText>
            <BodyText variant="muted" size="sm" className="line-clamp-2">
              {assignment.notes}
            </BodyText>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        {onView && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(assignment.id)}
          >
            View
          </Button>
        )}
        {onUnassign && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleUnassignClick}
            disabled={isUnassigning}
            className="text-error-600 border-error-600 hover:bg-error-50 hover:text-error-700 hover:border-error-700 disabled:opacity-50"
          >
            {isUnassigning ? 'Unassigning...' : 'Unassign'}
          </Button>
        )}
      </div>
    </Card>

    {/* Confirm Unassign Dialog */}
    <ConfirmDialog
      isOpen={showConfirmDialog}
      onClose={handleCancelUnassign}
      onConfirm={handleConfirmUnassign}
      title={unassignDialogTitle}
      message={unassignDialogMessage}
      confirmText="Unassign"
      cancelText="Cancel"
      destructive={true}
      isLoading={isUnassigning}
    />
    </>
  );
};